import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";

export interface ActiveFilter {
  key: string;
  label: string;
  value: any;
  displayValue: string;
  color?: string;
}

export interface FilterChipsProps {
  filters: ActiveFilter[];
  onRemove: (filterKey: string) => void;
  onClearAll: () => void;
}

export function FilterChips({ filters, onRemove, onClearAll }: FilterChipsProps) {
  if (filters.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: "auto" }}
        exit={{ opacity: 0, height: 0 }}
        transition={{ duration: 0.2 }}
        className="flex items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 overflow-x-auto"
      >
        <AnimatePresence mode="popLayout">
          {filters.map((filter) => {
            const isAdvanced = filter.color === "blue";

            return (
              <motion.span
                key={filter.key}
                layout
                initial={{ opacity: 0, scale: 0.85 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.85 }}
                transition={{ duration: 0.15 }}
                className={cn(
                  "inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium rounded-full border transition-colors",
                  isAdvanced
                    ? "bg-blue-50 border-blue-200 text-blue-700 hover:border-red-300 hover:bg-red-50"
                    : "bg-white border-gray-200 text-gray-700 hover:border-red-300 hover:bg-red-50"
                )}
              >
                <span className="whitespace-nowrap">
                  {filter.label}: {filter.displayValue}
                </span>
                <X
                  className="w-3.5 h-3.5 text-gray-400 hover:text-red-500 cursor-pointer flex-shrink-0"
                  onClick={() => onRemove(filter.key)}
                />
              </motion.span>
            );
          })}
        </AnimatePresence>

        <span
          className="text-xs text-gray-500 hover:text-red-500 ml-auto whitespace-nowrap cursor-pointer select-none"
          onClick={onClearAll}
        >
          Clear all
        </span>
      </motion.div>
    </AnimatePresence>
  );
}

export default FilterChips;
